import React from 'react';
import { ModeContext } from './ModeContext';
import { fetchRecentDocumentsForTags } from '@/utils/doc';
import { filterDuplicateDocuments, logError } from '@utils/usefulFuncs';

const initialState = {
  question: '',
  agentName: '',
  tags: [],
  selectedDocs: [],
  selectedCsvDocs: [],
  noRetrieval: false,
  onlySources: [],
  excludeSources: [],
  invalidCsvDocIds: [],
  csvErrorByDocId: {},
  // Score feeds attached on the Data Analyst Agent form: each item wraps one `feed_inputs` wire
  // entry (see features/dataAnalystAgent/feedInputs.js). The invalid-id list and the per-id error
  // map mirror the CSV pair above; feed ids are strings where doc ids are numbers.
  selectedFeedInputs: [],
  invalidFeedIds: [],
  feedErrorById: {},
};

const defaultContextValue = {
  state: initialState,
  setFormStateValue: () => logError('setFormStateValue not initialized'),
  addSelectedDocs: () => logError('addSelectedDocs not initialized'),
  addFeedInputs: () => logError('addFeedInputs not initialized'),
};

const ACTIONS = {
  SET_VALUE: 'SET_VALUE',
  ADD_SELECTED_DOCS: 'ADD_SELECTED_DOCS',
  ADD_FEED_INPUTS: 'ADD_FEED_INPUTS',
  RESET: 'RESET',
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_VALUE: {
      const { key, value } = action.payload;
      return { ...state, [key]: value };
    }
    case ACTIONS.ADD_SELECTED_DOCS: {
      return { ...state, selectedDocs: filterDuplicateDocuments([...state.selectedDocs, ...action.value]) };
    }
    case ACTIONS.ADD_FEED_INPUTS: {
      // Same append-to-latest-state rule as ADD_SELECTED_DOCS: a multi-feed attach hands items up
      // one by one from inside a loop that outlives the render it started in, so a closure read of
      // the list would append every item onto the same stale copy. A live item with the same feed
      // id wins over the incoming one (the picker refuses duplicates); an item whose rows were
      // released after a kickoff is only a summary and is replaced by a fresh attach.
      const incoming = action.value.filter((item) => item && item.id);
      const incomingIds = new Set(incoming.map((item) => item.id));
      const kept = (state.selectedFeedInputs || []).filter((item) => !(item.rowsReleased && incomingIds.has(item.id)));
      const liveIds = new Set(kept.map((item) => item.id));
      const added = incoming.filter((item) => !liveIds.has(item.id));
      return { ...state, selectedFeedInputs: [...kept, ...added] };
    }
    case ACTIONS.RESET: {
      return action.value;
    }
    default: {
      return state;
    }
  }
};

function filterForNewTags(newTags, oldTags) {
  const companyTags = newTags.filter((tag) => tag.doc_tag_id !== null);
  const publicTags = newTags.filter((tag) => tag.company_id !== null);

  const oldCompanyTags = oldTags.filter((tag) => tag.doc_tag_id !== null);
  const oldPublicTags = oldTags.filter((tag) => tag.company_id !== null);

  const newCompanyTags = companyTags.filter((tag) => !oldCompanyTags.some((t) => t.doc_tag_id === tag.doc_tag_id));
  const newPublicTags = publicTags.filter((tag) => !oldPublicTags.some((t) => t.company_id === tag.company_id));

  return [...newCompanyTags, ...newPublicTags];
}

export const FormStateContext = React.createContext(defaultContextValue);

export const FormStateProvider = ({ initialStateParam, children }) => {
  const { autoSelectRecentDocuments } = React.useContext(ModeContext);
  const initialFormState = React.useMemo(() => initialStateParam || initialState, [initialStateParam]);
  const [state, dispatch] = React.useReducer(reducer, initialFormState);

  const setFormStateValue = React.useCallback(
    async (key, value) => {
      if (key === null) {
        return dispatch({ type: ACTIONS.RESET, value: initialFormState });
      }
      dispatch({ type: ACTIONS.SET_VALUE, payload: { key, value } });

      if (key === 'tags' && autoSelectRecentDocuments) {
        const newTags = filterForNewTags(value, state.tags);
        const recentDocs = await fetchRecentDocumentsForTags(newTags);
        dispatch({ type: ACTIONS.ADD_SELECTED_DOCS, value: recentDocs });
      }
    },
    [autoSelectRecentDocuments, dispatch, initialFormState, state.tags]
  );

  // Append-to-latest-state add: immune to stale-closure lost updates when several
  // async callers (e.g. concurrent per-ticker doc fetches) add before a re-render.
  const addSelectedDocs = React.useCallback(
    (docs) => {
      dispatch({ type: ACTIONS.ADD_SELECTED_DOCS, value: Array.isArray(docs) ? docs : [docs] });
    },
    [dispatch]
  );

  const addFeedInputs = React.useCallback(
    (items) => {
      dispatch({ type: ACTIONS.ADD_FEED_INPUTS, value: Array.isArray(items) ? items : [items] });
    },
    [dispatch]
  );

  const value = React.useMemo(
    () => ({ state, setFormStateValue, addSelectedDocs, addFeedInputs }),
    [state, setFormStateValue, addSelectedDocs, addFeedInputs]
  );
  return <FormStateContext.Provider value={value}>{children}</FormStateContext.Provider>;
};
